import React, { useState, useEffect } from 'react';
import { Package, ShoppingBag, TrendingUp, AlertTriangle, IndianRupee } from 'lucide-react';
import { getAdminStats, getOrders, getProducts } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [statsRes, ordersRes, productsRes] = await Promise.all([
                getAdminStats(),
                getOrders(),
                getProducts()
            ]);

            if (statsRes.success) setStats(statsRes.data);
            if (ordersRes.success) setRecentOrders(ordersRes.data.slice(0, 5));
            if (productsRes.success) {
                setLowStockProducts(productsRes.data.filter(p => p.stock_quantity < 10).slice(0, 5));
            }

            // Set default stats if API fails
            if (!statsRes.success) {
                setStats({
                    totalOrders: 0,
                    todayOrders: 0,
                    pendingOrders: 0,
                    totalProducts: 0,
                    lowStockProducts: 0,
                    totalRevenue: 0
                });
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            setError(error.message || 'Failed to load dashboard data');
            // Set default values on error
            setStats({
                totalOrders: 0,
                todayOrders: 0,
                pendingOrders: 0,
                totalProducts: 0,
                lowStockProducts: 0,
                totalRevenue: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            confirmed: 'bg-blue-100 text-blue-800',
            shipped: 'bg-purple-100 text-purple-800',
            delivered: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800',
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            {error && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
                    <p className="font-medium">⚠️ Warning: {error}</p>
                    <p className="text-sm mt-1">Showing default values. Please check your API connection.</p>
                    <button
                        onClick={loadData}
                        className="mt-2 text-sm underline hover:no-underline"
                    >
                        Retry
                    </button>
                </div>
            )}
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                    <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Total Orders */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <ShoppingBag className="w-6 h-6 text-blue-600" />
                            </div>
                            <span className="text-sm text-gray-500">All Time</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{stats?.totalOrders || 0}</h3>
                        <p className="text-gray-600 text-sm mt-1">Total Orders</p>
                    </div>

                    {/* Today's Orders */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                            </div>
                            <span className="text-sm text-gray-500">Today</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{stats?.todayOrders || 0}</h3>
                        <p className="text-gray-600 text-sm mt-1">Today's Orders</p>
                    </div>

                    {/* Total Products */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Package className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{stats?.totalProducts || 0}</h3>
                        <p className="text-gray-600 text-sm mt-1">Total Products</p>
                    </div>

                    {/* Pending Orders */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{stats?.pendingOrders || 0}</h3>
                        <p className="text-gray-600 text-sm mt-1">Pending Orders</p>
                    </div>
                </div>

                {/* Revenue Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-primary to-primary-strong rounded-lg shadow-md p-6 text-white">
                        <div className="flex items-center gap-3 mb-4">
                            <IndianRupee className="w-8 h-8" />
                            <h3 className="text-lg font-semibold">Total Revenue</h3>
                        </div>
                        <p className="text-4xl font-bold">₹{(stats?.totalRevenue || 0).toLocaleString('en-IN')}</p>
                        <p className="text-white/80 text-sm mt-2">All time earnings</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
                        <div className="flex items-center gap-3 mb-4">
                            <TrendingUp className="w-8 h-8" />
                            <h3 className="text-lg font-semibold">Today's Revenue</h3>
                        </div>
                        <p className="text-4xl font-bold">₹{(stats?.todayRevenue || 0).toLocaleString('en-IN')}</p>
                        <p className="text-white/80 text-sm mt-2">Today's earnings</p>
                    </div>
                </div>

                {/* Recent Orders & Low Stock */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Orders */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-bold mb-4">Recent Orders</h2>
                        {recentOrders.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No orders yet</p>
                        ) : (
                            <div className="space-y-3">
                                {recentOrders.map((order) => (
                                    <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-sm">{order.order_number || order.orderNumber || 'N/A'}</p>
                                            <p className="text-xs text-gray-500">{order.customer_name || order.customerName || 'Unknown'}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-sm">₹{(order.total_amount || order.totalAmount || 0).toLocaleString('en-IN')}</p>
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(order.status || 'pending')}`}>
                                                {order.status || 'pending'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Low Stock Products */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-bold mb-4">Low Stock Alert</h2>
                        {lowStockProducts.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">All products well stocked</p>
                        ) : (
                            <div className="space-y-3">
                                {lowStockProducts.map((product) => (
                                    <div key={product.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <img src={product.image} alt={product.name} className="w-10 h-10 object-cover rounded" />
                                            <div>
                                                <p className="font-medium text-sm">{product.name || 'Unknown Product'}</p>
                                                <p className="text-xs text-gray-500">{product.category || 'Uncategorized'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-red-600">{product.stock_quantity || product.stockQuantity || 0} left</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
